import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSubscription } from '../hooks/useSubscription';
import ManagePanel from '../components/subscription/ManagePanel';
import CheckoutModal from '../components/subscription/CheckoutModal';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';

const ManageSubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { 
    currentSubscription, 
    plans, 
    loading, 
    error, 
    loadSubscriptionForManagement, 
    refreshSubscriptionData,
    loadPlans 
  } = useSubscription();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [actionType, setActionType] = useState('new');
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  // Load data on component mount
  useEffect(() => {
    loadPlans();
    loadSubscriptionForManagement();
  }, [loadPlans, loadSubscriptionForManagement]);

  // Initialize Stripe
  useEffect(() => {
    const initStripe = async () => {
      try {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (publishableKey) {
          const stripeInstance = await loadStripe(publishableKey);
          setStripe(stripeInstance);
        }
      } catch (error) {
      } finally {
        setStripeLoading(false);
      }
    };

    initStripe();
  }, []);

  // Show success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message);
      // Clear the message from state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleUpgrade = () => {
    // Navigate to pricing page instead of showing checkout modal
    navigate('/pricing');
  };

  const handleCheckoutSuccess = (result) => {
    setShowCheckout(false);
    setSelectedPlan(null);
    
    // Use polling for paid subscriptions to wait for webhook processing
    const usePolling = result.type === 'paid';
    refreshSubscriptionData(usePolling);
    
    // Show success message
    if (result.type === 'free') {
      toast.success('Plan changed successfully!');
    } else {
      toast.success('Payment successful! Your plan is being updated...');
    }
  };

  const handleCheckoutError = (error) => {
    toast.error(error.message || 'Failed to process payment');
  };

  const handleCheckoutClose = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to load subscription</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-[#BD2026] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a1f] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Subscription Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage your subscription, billing, and plan changes
              </p>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-[#BD2026] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#9a1a1f] transition-colors"
            >
              View All Plans
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Management Panel */}
          <div className="lg:col-span-2">
            <ManagePanel
              subscription={currentSubscription}
              plans={plans}
              onUpgrade={handleUpgrade}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-[#BD2026] text-white py-2 px-4 rounded-lg font-medium hover:bg-[#9a1a1f] transition-colors"
                >
                  Browse Plans
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  Contact Support
                </button>
              </div>
            </div>

            {/* Current Plan Summary */}
            {currentSubscription && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Plan</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Plan:</span>
                    <p className="text-gray-800 font-medium">
                      {plans.find(p => p.planCode === currentSubscription.planCode)?.name || currentSubscription.planCode}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Status:</span>
                    <p className="text-gray-800">{currentSubscription.status}</p>
                  </div>
                  {currentSubscription.currentPeriodEnd && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Next Billing:</span>
                      <p className="text-gray-800">
                        {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Help & Support */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Need Help?</h3>
              <p className="text-blue-700 text-sm mb-4">
                Have questions about your subscription or need to make changes?
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/contact')}
                  className="w-full bg-[#BD2026] text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-[#9a1a1f] transition-colors"
                >
                  Contact Support
                </button>
                <button
                  onClick={() => window.open('/faq', '_blank')}
                  className="w-full bg-blue-100 text-blue-800 py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                >
                  View FAQ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      {selectedPlan && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={handleCheckoutClose}
          plan={selectedPlan}
          stripe={stripe}
          onSuccess={handleCheckoutSuccess}
          onError={handleCheckoutError}
          actionType={actionType}
        />
      )}

      {/* Stripe Loading Overlay */}
      {stripeLoading && (
        <div className="fixed inset-0 z-50 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Initializing payment system...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionPage;
